import React, { useState } from "react";
import { toast } from "sonner";
import { Save, Bell, Globe, CalendarClock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { tzList, detectTimezone } from "../lib/timezones";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

export default function ClientSettings() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [tz, setTz] = useState(user?.timezone || detectTimezone());
  const [phone, setPhone] = useState(user?.phone || "");
  const [np, setNp] = useState({
    email: user?.notif_prefs?.email !== false,
    in_app: user?.notif_prefs?.in_app !== false,
    whatsapp: !!user?.notif_prefs?.whatsapp,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/settings", { timezone: tz, phone, notif_prefs: np });
      await refresh();
      toast.success(t("booking.settings_saved"));
    } catch { toast.error(t("booking.settings_err")); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8" data-testid="client-settings">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("booking.account_settings")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("booking.account_desc")}</p>

      <div className="mt-8 space-y-8">
        <div className="rounded-2xl border border-border bg-white p-6">
          <Label className="flex items-center gap-2 text-slateink"><Globe className="h-4 w-4 text-terracotta" />{t("booking.your_timezone")}</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger className="mt-1.5 max-w-xs" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{tzList().map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
          </Select>
          <div className="mt-5">
            <Label>{t("booking.phone")}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 max-w-xs" data-testid="settings-phone" />
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6">
          <h2 className="flex items-center gap-2 font-display font-semibold text-slateink"><Bell className="h-4 w-4 text-terracotta" />{t("booking.notif_prefs")}</h2>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-3 text-sm"><Checkbox checked={np.email} onCheckedChange={(v) => setNp((p) => ({ ...p, email: !!v }))} data-testid="notif-email" />{t("booking.notif_email")}</label>
            <label className="flex items-center gap-3 text-sm"><Checkbox checked={np.in_app} onCheckedChange={(v) => setNp((p) => ({ ...p, in_app: !!v }))} data-testid="notif-inapp" />{t("booking.notif_inapp")}</label>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("booking.reminders_note")}</p>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6" data-testid="reminder-preview">
          <h2 className="flex items-center gap-2 font-display font-semibold text-slateink"><CalendarClock className="h-4 w-4 text-terracotta" />{t("booking.reminder_preview_title")}</h2>
          <div className="mt-4 space-y-4 text-sm">
            <div>
              <p className="font-medium text-slateink">{t("booking.reminder_email_title")}</p>
              {np.email ? (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
                  <li>{t("booking.reminder_email_24")}</li>
                  <li>{t("booking.reminder_email_3")}</li>
                </ul>
              ) : <p className="mt-1 text-muted-foreground">{t("booking.reminder_email_off")}</p>}
            </div>
            <div>
              <p className="font-medium text-slateink">{t("booking.reminder_inapp_title")}</p>
              <p className="mt-1 text-muted-foreground">{np.in_app ? t("booking.reminder_inapp_desc") : t("booking.reminder_inapp_off")}</p>
            </div>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="settings-save">
          <Save className="mr-2 h-4 w-4" />{saving ? t("common.saving") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}
